import { Board } from "@/types/board";
import { ClientCoach, PageProps } from "@/types/page";
import { usePageProps } from "@/utils/dataLayer";
import { onUserEvent } from "@/utils/EventListener";
import axios, { AxiosError } from "axios";
import { PropsWithChildren, useEffect, useState } from "react";
import { BoardAccessContext } from "./BoardAccessContext";

export function BoardAccessProvider({ children }: PropsWithChildren) {
    const [goalsBoard, setGoalsBoard] = useState<Board | null>(null);
    const { user, clientCoach } =
        usePageProps<PageProps<{ clientCoach?: ClientCoach }>>();

    const otherUserId =
        user?.type === "coach"
            ? clientCoach?.client.id
            : user?.type === "client"
              ? clientCoach?.coach.id
              : undefined;

    const loadGoalsBoard = async () => {
        if (
            !user?.email_verified_at ||
            (user.type === "coach" && !clientCoach)
        ) {
            if (goalsBoard) {
                setGoalsBoard(null);
            }
            return;
        }
        try {
            const { data } = await axios.get<Board>(
                route("board.get", { other_user_id: otherUserId }),
            );
            setGoalsBoard(data);
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 404) {
                setGoalsBoard(null);
            } else {
                throw error;
            }
        }
    };

    onUserEvent<{ board_id: number }>("BoardUnlocked", (e) => {
        if (e.board_id === goalsBoard?.id) {
            loadGoalsBoard();
        }
    });

    onUserEvent<never>("ClientCoachCreatedBroadcastEvent", () => {
        loadGoalsBoard();
    });

    useEffect(() => {
        loadGoalsBoard();
    }, [user?.id, user?.type, user?.email_verified_at, otherUserId]);

    return (
        <BoardAccessContext.Provider
            value={{
                goalsBoard,
            }}
        >
            {children}
        </BoardAccessContext.Provider>
    );
}
